<template>
  <div class="dealer-settings-tab">
    <!-- Brand + Dealer selector: only shown when there are multiple dealers (i.e. importer) -->
    <div v-if="allSettings.length > 1" class="dealer-settings-tab__selector">
      <div class="ds-form__field">
        <label for="dealer-settings-brand">Merk</label>
        <select
          id="dealer-settings-brand"
          class="ds-field"
          :value="selectedBrand"
          @change="onBrandChange"
        >
          <option value=""></option>
          <option v-for="brand in availableBrands" :key="brand.code" :value="brand.code">
            {{ brand.name }}
          </option>
        </select>
      </div>
      <div class="ds-form__field">
        <label for="dealer-settings-dealer">Dealer</label>
        <input
          id="dealer-settings-dealer"
          class="ds-field"
          list="dealer-options"
          :value="selectedDealerId"
          :disabled="!selectedBrand"
          placeholder="Zoek op dealer ID..."
          @change="onDealerChange"
        />
        <datalist id="dealer-options">
          <option v-for="dealerId in dealersForSelectedBrand" :key="dealerId" :value="dealerId" />
        </datalist>
      </div>
    </div>

    <div v-if="loading" class="ds-empty">
      <p class="ds-empty__message">Instellingen laden…</p>
    </div>
    <div v-else-if="error" class="ds-empty">
      <p class="ds-empty__message">{{ error }}</p>
      <ds-button variant="ghost" data-qa="dealer-settings-retry-button" @click="load">
        Opnieuw proberen
      </ds-button>
    </div>

    <template v-else-if="form && Object.keys(form).length > 0">
      <div
        v-for="group in settingGroups"
        :key="group.label"
        class="dealer-settings-tab__group ds-card"
      >
        <div class="ds-card__header">{{ group.label }}</div>
        <div>
          <div
            v-for="setting in group.settings"
            v-show="displayFilter(setting)"
            :key="setting.key"
            class="ds-row ds-row--2col"
          >
            <span class="ds-row__label">{{ setting.label }}</span>
            <span class="dealer-settings-tab__control">
              <label v-if="setting.type === 'boolean'" class="ds-toggle">
                <input
                  type="checkbox"
                  :checked="!!form[setting.key]"
                  :disabled="disabledFilter(setting)"
                  @change="onBooleanChange(setting, $event.target.checked)"
                />
                <span class="ds-toggle__track"></span>
                <span class="ds-toggle__label">{{ form[setting.key] ? 'Aan' : 'Uit' }}</span>
              </label>
              <input
                v-else-if="setting.type === 'number'"
                type="number"
                class="ds-field"
                :value="form[setting.key]"
                :disabled="!editMode"
                @input="
                  form[setting.key] =
                    $event.target.value === '' ? null : Number($event.target.value)
                "
              />
              <select
                v-else-if="setting.type === 'select'"
                class="ds-field"
                :value="form[setting.key] || ''"
                :disabled="disabledFilter(setting)"
                @change="form[setting.key] = $event.target.value || null"
              >
                <option v-if="!form[setting.key]" value="">(niet ingesteld)</option>
                <option v-for="option in setting.options" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </span>
          </div>
        </div>
      </div>

      <div class="dealer-settings-tab__actions">
        <template v-if="!editMode">
          <ds-button data-qa="dealer-settings-edit-button" @click="editMode = true">
            <ds-icon name="edit" />
            Bewerken
          </ds-button>
        </template>
        <template v-else>
          <ds-button data-qa="dealer-settings-save-button" :disabled="saving" @click="save">
            <ds-icon name="save" />
            {{ saving ? 'Opslaan…' : 'Opslaan' }}
          </ds-button>
          <ds-button
            variant="ghost"
            data-qa="dealer-settings-cancel-button"
            :disabled="saving"
            @click="cancel"
          >
            Annuleren
          </ds-button>
        </template>
        <span v-if="saveSuccess" class="dealer-settings-tab__save-ok">&#x2713; Opgeslagen</span>
      </div>
    </template>

    <div v-else-if="allSettings.length > 1 && !selectedDealerId" class="ds-empty">
      <p class="ds-empty__message">Selecteer een merk en dealer om de instellingen te bekijken.</p>
    </div>
  </div>
</template>

<script>
import settingsApi from '@/api/server/Settings.api'
import Store from '@/store/store'
import { mapGetters } from 'vuex'
import { getBrandNameByCode } from '@/helpers/BrandHelper'
import DsButton from '@/components/ds/atoms/dsButton/DsButton.vue'
import DsIcon from '@/components/ds/atoms/dsIcon/DsIcon.vue'

const SETTING_GROUPS = [
  {
    label: 'Algemeen',
    settings: [
      { key: 'QuoteValidDays', label: 'Offerte geldig (dagen)', type: 'number' },
      { key: 'StockCarReservationDays', label: 'Voorraadauto reserveringsdagen', type: 'number' },
    ],
  },
  {
    label: 'Functies',
    settings: [
      { key: 'ChangeOrderEnabled', label: 'Wijzigingsorder', type: 'boolean' },
      { key: 'NewStockFlowEnabled', label: 'Nieuwe voorraad flow', type: 'boolean' },
      { key: 'StockMatchingPopupEnabled', label: 'Voorraad matching popup', type: 'boolean' },
      { key: 'ShowDeliveryDates', label: 'Levertijden tonen', type: 'boolean' },
      { key: 'ClaimOwnStockCarEnabled', label: 'Eigen voorraadauto claimbaar', type: 'boolean' },
      { key: 'PdfSigningEnabled', label: 'PDF ondertekening', type: 'boolean' },
      { key: 'SalesApprovalEnabled', label: 'Verkoop goedkeuring', type: 'boolean' },
      { key: 'IsCanAddMultipleExchangeCars', label: "Meerdere inruilauto's", type: 'boolean' },
      { key: 'AutolineKeyloopEnabled', label: 'Autoline Keyloop', type: 'boolean' },
      { key: 'NewStockMatchingEnabled', label: 'Nieuwe stock matching', type: 'boolean' },
      {
        key: 'PrivateLeasePrePaymentEnabled',
        label: 'Private lease vooruitbetaling ',
        type: 'boolean',
      },
      {
        key: 'DoNotDeliverBeforeEditEnabled',
        label: 'NLV datum bewerken',
        type: 'boolean',
      },
      {
        key: 'UseNewAccessoryCatalog',
        label: 'Nieuwe accessoire catalogus',
        type: 'boolean',
      },
      { key: 'IsImporter', label: 'Is importeur', type: 'boolean' },
      { key: 'VcrHidden', label: 'VCR en marges verbergen', type: 'boolean' },
      { key: 'VcrAutolineFormat', label: 'VCR in Autoline-format (pilot)', type: 'boolean' },
    ],
  },
  {
    label: 'VWPFS',
    settings: [
      { key: 'VwpfsFinanceEnabled', label: 'Financiering', type: 'boolean' },
      { key: 'VwpfsPrivateLeaseEnabled', label: 'Private Lease', type: 'boolean' },
      { key: 'VwpfsOperationalLeaseEnabled', label: 'Operationele Lease', type: 'boolean' },
      { key: 'VwpfsPlPriceDifferenceLimit', label: 'PL prijsverschil limiet (€)', type: 'number' },
      { key: 'VwpfsSilvaEnabled', label: 'Silva', type: 'boolean' },
      {
        key: 'VwpfsSilvaSendEstablishmentEnabled',
        label: 'Silva vestiging doorsturen',
        type: 'boolean',
      },
    ],
  },
  {
    // Insurance is no longer VWPFS-only, so it gets its own group and provider-neutral labels.
    label: 'Verzekering',
    settings: [
      { key: 'VwpfsInsuranceEnabled', label: 'Verzekering actief', type: 'boolean' },
      {
        key: 'InsuranceProvider',
        label: 'Verzekeraar',
        type: 'select',
        options: [
          { value: 'VWPFS', label: 'VWPFS' },
          { value: 'VMFS', label: 'Allianz (VMFS)' },
        ],
        // Which insurer to use is only meaningful once insurance itself is switched on.
        enabledWhen: 'VwpfsInsuranceEnabled',
      },
      {
        key: 'VwpfsInsuranceStandaardOn',
        label: 'Standaard aan in offerte',
        type: 'boolean',
        // Defaulting insurance to "on" in a quote only makes sense when insurance is enabled.
        enabledWhen: 'VwpfsInsuranceEnabled',
      },
    ],
  },
  {
    label: 'Bestuurders',
    settings: [
      { key: 'CommonDriversEnabledOnB2B', label: 'Vaste bestuurders op B2B', type: 'boolean' },
      { key: 'CommonDriversEnabledOnB2C', label: 'Vaste bestuurders op B2C', type: 'boolean' },
    ],
  },
  {
    label: 'Digitaal ondertekenen',
    settings: [{ key: 'ScriveGoEnabled', label: 'Scrive Go', type: 'boolean' }],
  },
]

const SELECT_KEYS = SETTING_GROUPS.flatMap((group) => group.settings)
  .filter((setting) => setting.type === 'select')
  .map((setting) => setting.key)

export default {
  name: 'DealerSettingsTab',
  components: { DsButton, DsIcon },
  data() {
    return {
      loading: false,
      saving: false,
      error: null,
      saveSuccess: false,
      editMode: false,
      form: {},
      original: {},
      settingGroups: SETTING_GROUPS,
      // Admin: all dealer settings indexed by brand -> [dealerIds]
      allSettings: [], // flat list of DealerSettingsDTO (with brand/dealerId)
      selectedBrand: '',
      selectedDealerId: '',
    }
  },

  computed: {
    ...mapGetters({
      isImporter: 'user/isImporter',
      canManageDealerSettings: 'user/canManageDealerSettings',
    }),
    availableBrands() {
      const brandCodes = [...new Set(this.allSettings.map((s) => s.Brand))]
      return brandCodes.map((code) => ({ code, name: getBrandNameByCode(code) }))
    },
    dealersForSelectedBrand() {
      return this.allSettings.filter((s) => s.Brand === this.selectedBrand).map((s) => s.DealerId)
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.error = null
      this.form = {}
      try {
        await this.loadAllDealerSettings()
      } catch (e) {
        this.error = 'Instellingen konden niet worden geladen. Probeer het opnieuw.'
      } finally {
        this.loading = false
      }
    },

    async loadAllDealerSettings() {
      const response = await settingsApi.getAllDealerSettings()
      this.allSettings = response.data || []
      if (this.allSettings.length > 0) {
        this.selectedBrand = this.allSettings[0].Brand
        this.selectedDealerId = this.allSettings[0].DealerId
        this.applySelectedDealerToForm()
      }
    },

    onBrandChange(event) {
      this.selectedBrand = event.target.value
      this.selectedDealerId = ''
      this.form = {}
      this.editMode = false
    },

    onDealerChange(event) {
      this.selectedDealerId = event.target.value
      this.editMode = false
      this.applySelectedDealerToForm()
    },

    applySelectedDealerToForm() {
      const match = this.allSettings.find(
        (s) => s.Brand === this.selectedBrand && s.DealerId === this.selectedDealerId
      )
      if (match) {
        this.mapResponseToForm(match)
      }
    },

    mapResponseToForm(data) {
      this.form = { ...data }
      this.original = { ...data }
    },

    displayFilter(setting) {
      if (['IsImporter', 'VcrHidden', 'VcrAutolineFormat'].includes(setting.key)) {
        return this.isImporter
      }
      if (setting.key === 'DoNotDeliverBeforeEditEnabled') {
        return this.canManageDealerSettings
      }
      return true
    },

    onBooleanChange(setting, checked) {
      this.form[setting.key] = checked
      if (!checked) {
        // Dependent booleans are reset so a stale "on" value can't be saved (and later take
        // effect again) while the setting it depends on is off. Selects keep their value.
        this.settingGroups
          .flatMap((group) => group.settings)
          .filter((s) => s.enabledWhen === setting.key && s.type === 'boolean')
          .forEach((s) => {
            this.form[s.key] = false
          })
      }
    },

    // Greys the field out rather than hiding it, so the stored value stays readable even when
    // the setting it depends on is switched off.
    disabledFilter(setting) {
      if (!this.editMode) return true
      if (setting.enabledWhen) return !this.form[setting.enabledWhen]
      return false
    },

    async save() {
      this.saving = true
      this.saveSuccess = false
      try {
        const payload = { ...this.form }
        SELECT_KEYS.forEach((key) => {
          if (payload[key] === '') payload[key] = null
        })

        const { data: updatedSettings } = await settingsApi.updateDealerSettings(payload)

        // If a dealer (not importer) saved their own settings
        // update the store directly from the response
        if (!this.isImporter) {
          Store.commit('user/setDealerSettings', updatedSettings)
        }

        // Refresh the cached entry so the dropdown stays in sync
        const idx = this.allSettings.findIndex(
          (s) => s.Brand === this.selectedBrand && s.DealerId === this.selectedDealerId
        )
        if (idx !== -1) {
          this.allSettings[idx] = { ...this.allSettings[idx], ...this.form }
        }

        this.original = { ...this.form }
        this.editMode = false
        this.saveSuccess = true
        setTimeout(() => {
          this.saveSuccess = false
        }, 3000)
      } catch (e) {
        this.error = 'Opslaan mislukt. Probeer het opnieuw.'
      } finally {
        this.saving = false
      }
    },

    cancel() {
      this.form = { ...this.original }
      this.editMode = false
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout only. The cards, rows, fields, toggle and buttons come from the
// design system (styles/ds/). See styles/ds/README.md before adding anything
// here.

.dealer-settings-tab__selector {
  display: flex;
  align-items: flex-end;
  gap: var(--ds-space-5);
  flex-wrap: wrap;
  margin-bottom: var(--ds-space-6);
  padding-bottom: var(--ds-space-5);
  border-bottom: 1px solid var(--ds-color-border);

  .ds-form__field {
    flex: 0 0 auto;
    min-width: 200px;
  }
}

.dealer-settings-tab__group {
  margin-bottom: 26px;
}

.dealer-settings-tab__control {
  display: flex;
  justify-content: flex-end;

  .ds-field {
    max-width: 280px;
  }

  textarea.ds-field {
    max-width: 100%;
  }
}

.dealer-settings-tab__actions {
  display: flex;
  align-items: center;
  gap: var(--ds-space-3);
  margin-top: 6px;
}

.dealer-settings-tab__save-ok {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--ds-color-success);
}
</style>
